import {
  companySizeLabel,
  companyStageLabel,
  fmtDate,
  fmtDayTime,
  isAccountFeeSettled,
  regionLabel,
  relativeDate,
} from '@remote-medium/shared';
import {
  ApiRequestError,
  Button,
  ErrorState,
  LoadingState,
  RoleForm,
  errorMessage,
  unwrap,
  useApi,
} from '@remote-medium/ui';
import { Check, Globe, Plus, Ticket } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router';

import { useBillingEnabled } from '../../lib/billing.tsx';
import { admin } from '../../lib/client.ts';
import { CuratorNotes } from '../../lib/curator-notes.tsx';
import { CompanyStatusChip } from '../../lib/status-chip.tsx';
import {
  Card,
  ConfirmButton,
  ExternalLink,
  None,
  Notice,
  Page,
  PageHeader,
  SectionHeader,
} from '../../lib/ui.tsx';
import { Activity } from './components/Activity.tsx';
import { CompanyRoles } from './components/CompanyRoles.tsx';
import { GrantCredits } from './components/GrantCredits.tsx';
import { loadCompanyContext } from './components/context.ts';

function Gate({
  title,
  done,
  detail,
  action,
}: {
  title: string;
  done: boolean;
  detail: ReactNode;
  action?: ReactNode;
}) {
  return (
    <li className="flex items-start gap-3 border-t border-border py-3 first:border-t-0 first:pt-0">
      <span
        aria-hidden="true"
        className={
          done
            ? 'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-success-subtle text-success-subtle-foreground'
            : 'mt-0.5 size-5 shrink-0 rounded-full border border-dashed border-border'
        }
      >
        {done && <Check className="size-3" />}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-semibold">
          {title}
          <span className="sr-only">{done ? ' (done)' : ' (not done)'}</span>
        </span>
        <span className="text-sm text-muted-foreground">{detail}</span>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </li>
  );
}

export function CompanyDetailPage() {
  const billing = useBillingEnabled();
  const { id } = useParams();
  const { data, error, loading, setData, reload } = useApi(
    () => unwrap(admin.companies[':id'].$get({ param: { id: id! } })),
    [id],
  );
  const context = useApi(() => loadCompanyContext(id!), [id]);
  const { data: taxonomy } = useApi(() => unwrap(admin.taxonomy.$get()), []);
  const [posting, setPosting] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);

  if (loading) return <LoadingState />;
  if (error || !data) return <ErrorState error={error} />;
  const c = data;

  // Errors surface inside ConfirmButton's dialog; plain buttons use the notice.
  const update = async (fn: () => Promise<typeof data>, ok: string) => {
    setMsg(null);
    setData(await fn());
    setMsg({ tone: 'info', text: ok });
  };
  const act = (fn: () => Promise<typeof data>, ok: string) =>
    update(fn, ok).catch((e: unknown) => setMsg({ tone: 'error', text: errorMessage(e) }));

  const grant = (input: { credits: number; note: string }) =>
    update(
      () => unwrap(admin.companies[':id'].credits.$post({ param: { id: c.id }, json: input })),
      `${input.credits === 1 ? '1 credit' : `${input.credits} credits`} granted.`,
    );

  const addNote = async (body: string) => {
    setSavingNote(true);
    setMsg(null);
    try {
      setData(
        await unwrap(admin.companies[':id'].notes.$post({ param: { id: c.id }, json: { body } })),
      );
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setSavingNote(false);
    }
  };

  const setSuspended = (suspended: boolean) =>
    unwrap(admin.companies[':id'].status.$post({ param: { id: c.id }, json: { suspended } }));

  const feeSettled = isAccountFeeSettled(c);
  // While billing is paused (Q65) activation exempts the fee, so before the
  // sanity check it isn't a gate. A company activated while billing was on
  // with the fee unpaid still needs it settled: "Mark not owed" does that.
  const feeDone = feeSettled || (!billing && !c.activatedAt);
  const gatesDone = !!c.activatedAt && feeSettled;
  const activate = (ok: string) =>
    act(() => unwrap(admin.companies[':id'].activate.$post({ param: { id: c.id } })), ok);
  const feeDetail = c.accountFeePaidAt
    ? `Paid ${fmtDayTime(c.accountFeePaidAt)}`
    : c.feeWaivedAt
      ? `Waived by ${c.feeWaivedByName ?? 'a curator'} · ${fmtDayTime(c.feeWaivedAt)}`
      : c.feeExemptAt
        ? `Not owed: activated during launch · ${fmtDayTime(c.feeExemptAt)}`
        : !billing
          ? c.activatedAt
            ? 'Not paid, from before billing was turned off. Mark it not owed to activate them.'
            : 'Not needed while billing is off. Marking it checked activates them for good.'
          : 'Not paid yet.';
  const totalCredits = c.bundles.reduce((n, b) => n + b.totalCredits, 0);
  const roles = context.data?.roles ?? [];
  // Roles that will try to spend a credit on their first introduction.
  const needCredit =
    billing && c.creditsRemaining === 0
      ? roles.filter(
          (r) =>
            !!r.matches &&
            r.matches.confirmed > 0 &&
            r.matches.introduced === 0 &&
            r.role.status !== 'closed',
        )
      : [];

  const meta: ReactNode[] = [
    c.website ? (
      <ExternalLink href={c.website}>{c.website.replace(/^https?:\/\//, '')}</ExternalLink>
    ) : (
      'No website'
    ),
  ];
  const profile = [
    c.stage && companyStageLabel(c.stage),
    c.sizeBand && companySizeLabel(c.sizeBand),
  ]
    .filter(Boolean)
    .join(' · ');
  if (profile) meta.push(profile);
  meta.push(
    <span className="inline-flex items-center gap-1.5">
      <Globe aria-hidden="true" className="size-3.5" />
      {c.hqCountry ? regionLabel(c.hqCountry) : 'HQ unknown'}
    </span>,
  );
  meta.push(
    `Joined ${relativeDate(c.createdAt)}${
      gatesDone
        ? ` · Activated by ${c.activatedByName ?? 'a curator'}, fee ${c.accountFeePaidAt ? 'paid' : c.feeWaivedAt ? 'waived' : 'not owed (launch)'}`
        : ''
    }`,
  );

  return (
    <Page>
      <PageHeader
        back={{ to: '/companies', label: 'Companies' }}
        title={c.name}
        status={<CompanyStatusChip status={c.status} />}
        description={
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {meta.map((m, i) => (
              <span key={i}>{m}</span>
            ))}
          </div>
        }
      />

      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}

      {needCredit.length > 0 && (
        <div
          role="status"
          className="flex items-start gap-3 rounded-lg border border-warning/40 bg-warning-subtle px-4 py-3 text-sm text-warning-subtle-foreground"
        >
          <Ticket aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="font-medium">
              {needCredit.length === 1 && needCredit[0]!.role.status === 'shortlisted'
                ? `${needCredit[0]!.role.title} is shortlisted`
                : `${needCredit.map((r) => r.role.title).join(', ')} ${needCredit.length === 1 ? 'has' : 'have'} confirmed candidates`}
              , but they have 0 role credits
            </span>
            <span>
              When a candidate says yes, the first introduction will fail and nothing will be sent.
              They need {needCredit.length === 1 ? '1 credit' : `${needCredit.length} credits`}{' '}
              before then.
            </span>
          </div>
          <div className="shrink-0">
            <GrantCredits
              companyName={c.name}
              suggested={needCredit.length}
              variant="default"
              onGrant={grant}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
        <div className="flex flex-col gap-8">
          {!gatesDone && (
            <Card>
              <SectionHeader
                title="Before they can post"
                aside={billing ? 'Both must be done' : 'Billing is off'}
              />
              <ul className="m-0 flex list-none flex-col p-0">
                <Gate
                  title="Curator sanity check"
                  done={!!c.activatedAt}
                  detail={
                    c.activatedAt
                      ? `${c.activatedByName ?? 'A curator'} · ${fmtDayTime(c.activatedAt)}`
                      : 'Check the website and team look like a real hiring company.'
                  }
                  action={
                    !c.activatedAt && (
                      <Button size="sm" onClick={() => void activate('Sanity check recorded.')}>
                        Mark checked
                      </Button>
                    )
                  }
                />
                <Gate
                  title="Account fee"
                  done={feeDone}
                  detail={feeDetail}
                  action={
                    !feeDone &&
                    (!billing ? (
                      <Button
                        size="sm"
                        onClick={() => void activate('Account fee marked not owed.')}
                      >
                        Mark not owed
                      </Button>
                    ) : (
                      <ConfirmButton
                        title={`Waive the account fee for ${c.name}?`}
                        description="They skip payment and can post once the sanity check is done. You're recorded as the curator who waived it."
                        confirmLabel="Waive fee"
                        onConfirm={() =>
                          update(
                            () =>
                              unwrap(
                                admin.companies[':id']['waive-fee'].$post({ param: { id: c.id } }),
                              ),
                            'Account fee waived.',
                          )
                        }
                      >
                        Waive fee
                      </ConfirmButton>
                    ))
                  }
                />
              </ul>
            </Card>
          )}

          {context.error ? (
            <ErrorState error={context.error} />
          ) : (
            <CompanyRoles
              roles={roles}
              loading={context.loading}
              canPost={c.status === 'active'}
              action={
                c.status === 'active' &&
                !posting && (
                  <Button variant="outline" size="sm" onClick={() => setPosting(true)}>
                    <Plus data-icon="inline-start" />
                    Post on their behalf
                  </Button>
                )
              }
            />
          )}

          {posting && taxonomy && (
            <Card>
              <SectionHeader title={`Post a role for ${c.name}`} />
              <RoleForm
                taxonomy={taxonomy}
                submitLabel="Post role"
                onCancel={() => setPosting(false)}
                onSubmit={async (input) => {
                  try {
                    await unwrap(
                      admin.companies[':id'].roles.$post({ param: { id: c.id }, json: input }),
                    );
                    setPosting(false);
                    setMsg({ tone: 'info', text: 'Role posted.' });
                    reload();
                    context.reload();
                  } catch (e) {
                    setMsg({
                      tone: 'error',
                      text: e instanceof ApiRequestError ? e.message : 'Could not post the role.',
                    });
                  }
                }}
              />
            </Card>
          )}

          <Activity c={c} ctx={context.data} />
        </div>

        <div className="flex flex-col gap-6">
          <Card>
            <SectionHeader
              title="Role credits"
              actions={<GrantCredits companyName={c.name} onGrant={grant} />}
            />
            <p className="m-0 flex items-baseline gap-2">
              <span className="text-4xl font-medium tabular-nums">{c.creditsRemaining}</span>
              <span className="text-sm text-muted-foreground">
                left{totalCredits ? ` of ${totalCredits}` : ''}
              </span>
            </p>
            <p className="m-0 text-sm text-muted-foreground">
              {billing
                ? '1 credit is used when the first introduction for a role is sent. Later introductions on the same role are free.'
                : 'No credits are used while billing is off. Roles introduced now never use one.'}
            </p>
            {c.bundles.length > 0 ? (
              <ul className="m-0 flex list-none flex-col border-t border-border p-0">
                {c.bundles.map((b) => (
                  <li
                    key={b.id}
                    className="flex items-start justify-between gap-3 border-t border-border py-2.5 text-sm first:border-t-0"
                  >
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-muted-foreground">
                        {fmtDate(b.purchasedAt)} ·{' '}
                        {b.grant ? `Granted by ${b.grant.byName ?? 'a curator'}` : 'Bought'}
                      </span>
                      {b.grant?.note && (
                        <span className="text-xs text-pretty text-muted-foreground">
                          {b.grant.note}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 tabular-nums">
                      {b.remainingCredits} / {b.totalCredits}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="m-0 border-t border-border pt-4 text-sm text-muted-foreground">
                No credits bought or granted yet.
              </p>
            )}
          </Card>

          <Card>
            <SectionHeader title="Team" aside="Gets introduction emails" />
            {c.users.length ? (
              <ul className="m-0 flex list-none flex-col gap-4 p-0">
                {c.users.map((u) => (
                  <li key={u.id} className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold">
                      {u.fullName ?? <None>No name</None>}
                    </span>
                    <a
                      href={`mailto:${u.email}`}
                      className="w-fit truncate text-sm text-foreground underline underline-offset-4"
                    >
                      {u.email}
                    </a>
                    <span className="text-xs text-muted-foreground">
                      {u.lastSignInAt
                        ? `Last signed in ${relativeDate(u.lastSignInAt)}`
                        : 'Not signed in yet'}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <None>No users</None>
            )}
          </Card>

          <CuratorNotes
            notes={c.notes}
            hiddenFrom="the company"
            placeholder="How they hire, who to talk to, what to watch for."
            busy={savingNote}
            onAdd={addNote}
            onDelete={async (noteId) =>
              setData(
                await unwrap(
                  admin.companies[':id'].notes[':noteId'].$delete({ param: { id: c.id, noteId } }),
                ),
              )
            }
          />

          {c.status === 'suspended' ? (
            <Card>
              <SectionHeader title="Account suspended" />
              <p className="m-0 text-sm text-muted-foreground">
                They can't post roles or see candidates. Nothing was deleted.
              </p>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void act(() => setSuspended(false), 'Suspension lifted.')}
                >
                  Lift suspension
                </Button>
              </div>
            </Card>
          ) : (
            <Card>
              <SectionHeader title="Suspend account" />
              <p className="m-0 text-sm text-muted-foreground">
                Stops new posts and hides candidates from them. Nothing is deleted, and you can lift
                it at any time.
              </p>
              <div>
                <ConfirmButton
                  title={`Suspend ${c.name}?`}
                  description="They can't post roles or see candidates until you lift the suspension. Nothing is deleted."
                  confirmLabel="Suspend"
                  destructive
                  onConfirm={() => update(() => setSuspended(true), 'Company suspended.')}
                >
                  Suspend account…
                </ConfirmButton>
              </div>
            </Card>
          )}
        </div>
      </div>
    </Page>
  );
}
